import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import { LeafletMap } from '@/components/map/mapLeaflet.tsx';
import { Art14ClubModal } from '@/components/rgpd/Art14ClubModal';
import { ContactModal } from '@/components/ui/ContactModal';
import { ShareBloc } from '@/components/ui/ShareBloc';
import { ROUTES } from '@/config/routes';
import { getPublishedArticlesApi } from '@/lib/api/blogApi';
import {
  createMembershipRequestApi,
  getClubApi,
  getClubCompetitionsApi,
  getMyMembershipRequestApi,
  sendClubMessageApi,
} from '@/lib/api/clubsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store.ts';
import {
  type ClubCompetitionEntry,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
  formatDateFullFr,
  getInitials,
} from '@shared';
import { Role } from '@shared/enums/role.enum';
import type { Club } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  BadgeCheck,
  Calendar,
  Check,
  Clock,
  EyeOff,
  FileText,
  Flag,
  Info,
  Mail,
  MapPin,
  Phone,
  ShieldAlert,
  Trophy,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { ClaimAdminSection } from './member-profile/claim-admin-section';

// ─── Helpers ─────────────────────────────────────────────────
const BANNER_COLORS = [
  'from-emerald-500 via-emerald-600 to-teal-700',
  'from-blue-500 via-blue-600 to-indigo-700',
  'from-orange-500 via-orange-600 to-red-700',
  'from-purple-500 via-purple-600 to-pink-700',
  'from-cyan-500 via-cyan-600 to-blue-700',
  'from-amber-500 via-amber-600 to-orange-700',
  'from-indigo-500 via-indigo-600 to-purple-700',
];

const AVATAR_BG = [
  'bg-emerald-500',
  'bg-blue-500',
  'bg-orange-500',
  'bg-purple-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-indigo-500',
];

const TABS = ['Présentation', 'Concours', 'Blog', 'Coordonnées'] as const;
const SECTION_IDS = ['section-presentation', 'section-concours', 'section-blog', 'section-contact'];

// ─── Sub-components ───────────────────────────────────────────
function ClubCompetitionRow({ entry }: { entry: ClubCompetitionEntry }) {
  const isFinished = entry.status === 'FINISHED';

  return (
    <Link
      to={ROUTES.CONCOUR.replace(':id', String(entry.competitionId))}
      className='flex items-center justify-between bg-slate-50 rounded-2xl p-5 hover:bg-slate-100 transition-colors group'
    >
      <div className='flex items-center gap-4'>
        <div className='w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center shrink-0'>
          <Trophy className='w-6 h-6' />
        </div>
        <div>
          <h4 className='font-bold text-slate-900 group-hover:text-emerald-700 transition-colors'>
            {entry.competitionName}
          </h4>
          <div className='flex flex-wrap items-center gap-2 text-sm text-slate-500 mt-1'>
            <span className='flex items-center gap-1'>
              <Calendar className='w-3.5 h-3.5' />
              {formatDateFullFr(entry.competitionDate)}
            </span>
            {(entry.venue ?? entry.city) && (
              <span className='flex items-center gap-1'>
                <MapPin className='w-3.5 h-3.5' />
                {entry.venue ?? entry.city}
              </span>
            )}
            <span className='px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-md'>
              {TYPE_LABELS[entry.type as keyof typeof TYPE_LABELS] ?? entry.type}
            </span>
          </div>
        </div>
      </div>
      <div className='text-right shrink-0 ml-4'>
        <div className='text-xs text-slate-500'>{entry.teamName}</div>
        {isFinished && entry.rank != null ? (
          <div className='font-bold text-amber-600'>{entry.rank === 1 ? '1er' : `${entry.rank}e`}</div>
        ) : isFinished ? (
          <div className='text-xs text-slate-400'>Terminé</div>
        ) : (
          <div className='text-xs text-emerald-600 font-semibold'>En cours</div>
        )}
      </div>
    </Link>
  );
}

// ─── Join CTA widget ──────────────────────────────────────────
function JoinClubWidget({ clubId, clubName }: { clubId: number; clubName: string }) {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');

  const isMemberEligible = isAuthenticated && user?.role === Role.MEMBER && !user?.clubId;

  const { data: pendingRequest } = useQuery({
    queryKey: queryKeys.clubs.myMembershipRequest(),
    queryFn: getMyMembershipRequestApi,
    enabled: isMemberEligible,
  });

  const joinMutation = useMutation({
    mutationFn: (msg: string) => createMembershipRequestApi(clubId, msg ? { message: msg } : undefined),
    onSuccess: () => {
      toast.success('Demande envoyée ! Le club examinera votre demande.');
      setModalOpen(false);
      setMessage('');
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.myMembershipRequest() });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Erreur lors de l'envoi");
    },
  });

  // ── Non connecté
  if (!isAuthenticated) {
    return (
      <div className='bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white'>
        <h3 className='font-bold text-lg mb-2'>Rejoindre ce club</h3>
        <p className='text-emerald-100 text-sm mb-4'>
          Inscrivez-vous sur BoulesConnect pour envoyer une demande d&apos;adhésion à{' '}
          <span className='font-bold'>{clubName}</span>.
        </p>
        <div className='space-y-2'>
          <Link
            to={`${ROUTES.LOGIN}?redirectTo=${encodeURIComponent(`/clubs/${clubId}`)}`}
            className='w-full flex items-center justify-center gap-2 bg-white text-emerald-700 py-2.5 rounded-xl font-bold hover:bg-emerald-50 transition-colors text-sm'
          >
            Se connecter <ArrowRight className='w-4 h-4' />
          </Link>
          <Link
            to={ROUTES.REGISTER}
            className='w-full flex items-center justify-center gap-2 bg-white/20 text-white py-2.5 rounded-xl font-semibold hover:bg-white/30 transition-colors text-sm'
          >
            Créer un compte
          </Link>
        </div>
      </div>
    );
  }

  // ── Connecté mais pas joueur (organisateur, admin, etc.)
  if (user?.role !== Role.MEMBER) {
    return null;
  }

  // ── Joueur déjà dans un club
  if (user.clubId) {
    return (
      <div className='bg-slate-50 rounded-3xl border border-slate-200 p-6'>
        <h3 className='font-bold text-lg text-slate-900 mb-2'>Rejoindre ce club</h3>
        <p className='text-slate-500 text-sm'>
          Vous êtes déjà membre d&apos;un club. Quittez votre club actuel pour pouvoir en rejoindre un autre.
        </p>
      </div>
    );
  }

  const isPendingForThis = pendingRequest?.club?.id === clubId;
  const hasPendingElsewhere = !!pendingRequest && pendingRequest.club?.id !== clubId;

  // ── Demande en attente pour ce club
  if (isPendingForThis) {
    return (
      <div className='rounded-3xl border border-amber-200 bg-amber-50 p-6'>
        <div className='flex items-start gap-3 mb-3'>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100'>
            <Clock className='h-5 w-5 text-amber-600' />
          </div>
          <div>
            <h3 className='font-bold text-amber-900'>Demande en attente</h3>
            <p className='text-sm text-amber-700 mt-1'>
              Votre demande d&apos;adhésion à <span className='font-bold'>{clubName}</span> est en cours d&apos;examen.
            </p>
          </div>
        </div>
        <p className='text-xs text-amber-600'>Les administrateurs du club vous répondront prochainement.</p>
      </div>
    );
  }

  // ── Joueur sans club, formulaire de demande
  return (
    <>
      <div className='bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white'>
        <div className='flex items-center gap-2 mb-2'>
          <h3 className='font-bold text-lg'>Rejoindre ce club</h3>
        </div>
        <p className='text-emerald-100 text-sm mb-4'>
          Envoyez une demande d&apos;adhésion à <span className='font-semibold'>{clubName}</span>. Les admins du club
          examineront votre dossier.
        </p>
        {hasPendingElsewhere && (
          <div className='mb-4 rounded-xl bg-white/15 px-4 py-3 text-xs text-emerald-100'>
            Vous avez déjà une demande en attente pour <span className='font-bold'>{pendingRequest!.club?.name}</span>.
            Vous pouvez tout de même en envoyer une ici.
          </div>
        )}
        <button
          onClick={() => setModalOpen(true)}
          className='w-full flex items-center justify-center gap-2 bg-white text-emerald-700 py-3 rounded-xl font-bold hover:bg-emerald-50 transition-colors cursor-pointer'
        >
          Faire une demande d&apos;adhésion <ArrowRight className='w-5 h-5' />
        </button>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div
            className='absolute inset-0 bg-black/50 backdrop-blur-sm'
            onClick={() => {
              setModalOpen(false);
              setMessage('');
            }}
          />
          <div className='relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
            <div className='mb-5 flex items-start justify-between gap-3'>
              <div>
                <h2 className='text-base font-bold text-slate-900'>Rejoindre {clubName}</h2>
                <p className='mt-1 text-sm text-slate-500'>
                  Votre demande sera examinée par les administrateurs du club.
                </p>
              </div>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setMessage('');
                }}
                className='rounded-lg p-1 transition hover:bg-slate-100'
              >
                <X className='h-4 w-4 text-slate-400' />
              </button>
            </div>
            <div className='mb-5'>
              <label className='mb-1.5 block text-xs font-semibold text-slate-700'>Message (facultatif)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder='Présentez-vous ou expliquez votre démarche…'
                className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none'
              />
              <p className='mt-1 text-right text-xs text-slate-400'>{message.length}/500</p>
            </div>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setMessage('');
                }}
                disabled={joinMutation.isPending}
                className='rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
              >
                Annuler
              </button>
              <button
                onClick={() => joinMutation.mutate(message)}
                disabled={joinMutation.isPending}
                className='flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-50'
              >
                {joinMutation.isPending ? (
                  'Envoi…'
                ) : (
                  <>
                    <Check className='h-4 w-4' />
                    Envoyer la demande
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────
export default function ClubProfile() {
  const { id } = useParams<{ id: string }>();
  const clubId = Number(id);
  const [activeTab, setActiveTab] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const { user, isAuthenticated } = useAuthStore();

  const contactMutation = useMutation({
    mutationFn: (data: { fullName: string; message: string; hcaptchaToken: string }) =>
      sendClubMessageApi(clubId, data),
    onSuccess: () => {
      toast.success('Message envoyé');
      setContactOpen(false);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Erreur lors de l'envoi");
    },
  });

  const {
    data: club,
    isLoading,
    isError,
  } = useQuery<Club>({
    queryKey: queryKeys.clubs.detail(clubId),
    queryFn: () => getClubApi(clubId),
    enabled: !isNaN(clubId),
  });

  const { data: clubCompetitions = [] } = useQuery({
    queryKey: queryKeys.clubs.competitions(clubId),
    queryFn: () => getClubCompetitionsApi(clubId),
    enabled: !isNaN(clubId) && club?.isProfilePublic === true,
  });

  const { data: articlesData } = useQuery({
    queryKey: queryKeys.blog.articles({ clubId: clubId, limit: 4 }),
    queryFn: () => getPublishedArticlesApi({ clubId: clubId, limit: 4 }),
    enabled: !isNaN(clubId) && club?.isProfilePublic === true,
  });
  const articles = articlesData?.data ?? [];

  if (isLoading) {
    return (
      <div className='bg-[#F8FAFC] min-h-screen'>
        <div className='h-80 animate-pulse bg-slate-200' />
        <section className='px-6'>
          <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8 animate-pulse'>
              <div className='flex gap-8'>
                <div className='w-32 h-32 rounded-3xl bg-slate-200' />
                <div className='flex-1 space-y-4'>
                  <div className='h-8 bg-slate-200 rounded-xl w-1/2' />
                  <div className='h-4 bg-slate-200 rounded-xl w-1/3' />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isError || !club) {
    return (
      <div className='bg-[#F8FAFC] min-h-screen flex items-center justify-center'>
        {/* Club introuvable / inactif / privé (404 API) : noindex pour éviter
            le duplicate content sur une URL dynamique non publiable. */}
        <PageMeta
          title='Club introuvable'
          description="Ce club n'existe pas ou n'est pas accessible publiquement."
          robots='noindex, nofollow'
        />
        <div className='text-center'>
          <Flag className='w-16 h-16 text-slate-300 mx-auto mb-4' />
          <h2 className='text-2xl font-bold text-slate-700 mb-2'>Club introuvable</h2>
          <p className='text-slate-500 mb-6'>Ce club n&apos;existe pas ou a été supprimé.</p>
          <Link
            to={ROUTES.CLUBS}
            className='inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors'
          >
            Retour à l&apos;annuaire
          </Link>
        </div>
      </div>
    );
  }

  const colorIdx = club.id % BANNER_COLORS.length;
  const initials = getInitials(club.name);
  const logoFile = club.files?.find(f => f.usage === 'CLUB_LOGO') ?? null;
  const coverFile = club.files?.find(f => f.usage === 'CLUB_COVER') ?? null;

  // ── Private profile ──
  if (!club.isProfilePublic) {
    return (
      <div className='bg-[#F8FAFC] text-slate-900 font-sans min-h-screen'>
        <PageMeta title={club.name} description='Ce profil de club est privé.' robots='noindex, nofollow' />

        {/* Blurred hero */}
        <div className='relative'>
          <div
            className={`h-56 sm:h-64 md:h-80 bg-gradient-to-br ${BANNER_COLORS[colorIdx]} relative blur-sm opacity-60`}
          >
            <div className='absolute inset-0 opacity-20'>
              <div className='absolute top-10 right-20 w-40 h-40 rounded-full bg-white/20' />
              <div className='absolute bottom-10 left-10 w-32 h-32 rounded-full bg-white/10' />
            </div>
          </div>
        </div>

        {/* Skeleton card blurred */}
        <section className='px-6'>
          <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8'>
              <div className='flex flex-col md:flex-row gap-8'>
                <div
                  className={`w-32 h-32 ${AVATAR_BG[colorIdx]} rounded-3xl flex items-center justify-center text-white font-extrabold text-4xl shadow-lg shrink-0 blur-sm opacity-60`}
                >
                  {initials}
                </div>
                <div className='flex-1'>
                  <h1 className='text-3xl md:text-4xl font-extrabold text-slate-900 mb-3'>{club.name}</h1>
                  <div className='flex items-center gap-1.5 text-sm text-slate-500 mt-1'>
                    <MapPin className='w-3.5 h-3.5' />
                    {club.city}, {club.department}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Private message */}
        <section className='px-6 py-16'>
          <div className='max-w-2xl mx-auto'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-sm p-12 text-center'>
              <div className='w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6'>
                <EyeOff className='w-8 h-8 text-slate-400' />
              </div>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-3'>Profil privé</h2>
              <p className='text-slate-500 leading-relaxed mb-8'>
                Ce club a choisi de rendre son profil privé. Les informations détaillées ne sont pas accessibles
                publiquement.
              </p>
              <Link
                to={ROUTES.CLUBS}
                className='inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors'
              >
                Retour à l&apos;annuaire
              </Link>
            </div>
          </div>
        </section>

        {/* Blurred skeleton sections */}
        <section className='px-6 pb-16'>
          <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 blur-sm opacity-30 pointer-events-none select-none'>
            <div className='lg:w-2/3 space-y-8'>
              <div className='bg-white rounded-3xl border border-slate-200 p-8'>
                <div className='h-6 bg-slate-200 rounded-xl w-1/3 mb-4' />
                <div className='space-y-2'>
                  <div className='h-4 bg-slate-100 rounded w-full' />
                  <div className='h-4 bg-slate-100 rounded w-5/6' />
                  <div className='h-4 bg-slate-100 rounded w-4/6' />
                </div>
              </div>
              <div className='bg-white rounded-3xl border border-slate-200 p-8'>
                <div className='h-6 bg-slate-200 rounded-xl w-1/4 mb-4' />
                <div className='space-y-3'>
                  <div className='h-16 bg-slate-100 rounded-2xl' />
                  <div className='h-16 bg-slate-100 rounded-2xl' />
                </div>
              </div>
            </div>
            <div className='lg:w-1/3 space-y-6'>
              <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                <div className='h-5 bg-slate-200 rounded w-1/3 mb-4' />
                <div className='space-y-3'>
                  <div className='h-4 bg-slate-100 rounded w-full' />
                  <div className='h-4 bg-slate-100 rounded w-3/4' />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title={club.name}
        description={`Club de pétanque ${club.name}${club.city ? ` — ${club.city}` : ''}. Retrouvez les concours à venir et rejoignez le club.`}
        image={(coverFile ?? logoFile) ? getFileUrl((coverFile ?? logoFile)!) : undefined}
        ogType='profile'
        // Défense : en public l'API 404 déjà les clubs inactifs (→ page
        // « introuvable » noindex). Ce cas ne se présente donc que pour un
        // viewer privilégié (admin) ; on garde le noindex par sécurité.
        robots={club.isActive ? undefined : 'noindex, nofollow'}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'SportsOrganization',
          name: club.name,
          sport: 'Pétanque',
          url: `${SITE_URL}/clubs/${club.id}`,
          ...(club.description ? { description: club.description } : {}),
          ...(logoFile ? { logo: getFileUrl(logoFile) } : {}),
          ...(club.address || club.city
            ? {
                address: {
                  '@type': 'PostalAddress',
                  ...(club.address ? { streetAddress: club.address } : {}),
                  ...(club.city ? { addressLocality: club.city } : {}),
                  ...(club.department ? { addressRegion: club.department } : {}),
                  addressCountry: 'FR',
                },
              }
            : {}),
          ...(club.phone ? { telephone: club.phone } : {}),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Clubs', item: `${SITE_URL}/clubs` },
            { '@type': 'ListItem', position: 3, name: club.name, item: `${SITE_URL}/clubs/${club.id}` },
          ],
        }}
      />
      {/* Hero Cover */}
      {coverFile ? (
        <img
          src={getFileUrl(coverFile)}
          alt={club.name}
          className='w-full h-56 sm:h-64 md:h-80 object-cover object-top'
        />
      ) : (
        <div className={`h-56 sm:h-64 md:h-80 bg-gradient-to-br ${BANNER_COLORS[colorIdx]} relative`}>
          <div className='absolute inset-0 opacity-20'>
            <div className='absolute top-10 right-20 w-40 h-40 rounded-full bg-white/20' />
            <div className='absolute bottom-10 left-10 w-32 h-32 rounded-full bg-white/10' />
            <div className='absolute top-20 left-1/3 w-24 h-24 rounded-full bg-white/10' />
          </div>
        </div>
      )}

      {/* Profile Card */}
      <section className='px-6'>
        <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
          <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8'>
            <div className='flex flex-col md:flex-row gap-8'>
              {/* Logo */}
              {logoFile ? (
                <img
                  src={getFileUrl(logoFile)}
                  alt={club.name}
                  className='w-32 h-32 rounded-3xl object-cover shadow-lg shrink-0'
                />
              ) : (
                <div
                  className={`w-32 h-32 ${AVATAR_BG[colorIdx]} rounded-3xl flex items-center justify-center text-white font-extrabold text-4xl shadow-lg shrink-0`}
                >
                  {initials}
                </div>
              )}

              {/* Info */}
              <div className='flex-1'>
                <h1 className='text-3xl md:text-4xl font-extrabold text-slate-900 mb-3'>{club.name}</h1>

                <div className='flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4'>
                  <span className='flex items-center gap-1.5'>
                    <MapPin className='w-4 h-4' />
                    {club.city}, {club.department}
                  </span>
                  {club.region && (
                    <span className='flex items-center gap-1.5'>
                      <Flag className='w-4 h-4' />
                      {club.region}
                    </span>
                  )}
                </div>

                <div className='flex flex-wrap items-center gap-3 mb-6'>
                  {club.isActive ? (
                    <span className='inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-full text-xs font-bold'>
                      <BadgeCheck className='w-3.5 h-3.5' /> Club actif
                    </span>
                  ) : (
                    <span className='inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 border border-slate-200 px-3 py-1.5 rounded-full text-xs font-bold'>
                      Inactif
                    </span>
                  )}
                  {club.registrationNumber && (
                    <span className='inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-full text-xs font-bold'>
                      N° {club.registrationNumber}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Tab Nav */}
      <nav className='sticky top-16 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200 mt-8'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='flex items-center gap-1 overflow-x-auto py-3'>
            {TABS.map((tab, i) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(i);
                  const el = document.getElementById(SECTION_IDS[i]);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === i ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* RGPD Art. 14 footer */}
      <div className='bg-slate-50 border-t border-slate-200 px-6 py-4'>
        <div className='max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500'>
          <div className='flex items-center gap-4'>
            <Art14ClubModal
              trigger={
                <span className='inline-flex items-center gap-1 hover:text-blue-600 transition-colors underline'>
                  <Info className='w-3.5 h-3.5' />
                  D&apos;ou proviennent ces informations ?
                </span>
              }
            />
          </div>
          <Link
            to={`/contact?subject=${encodeURIComponent(`Demande de retrait de fiche club`)}&message=${encodeURIComponent(`Bonjour,\n\nJe souhaite demander le retrait de la fiche du club "${club.name}" (ID: ${club.id}) de l'annuaire BoulesConnect.\n\nMotif : [merci de preciser]\n\nCordialement`)}`}
            className='inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 font-medium transition-colors'
          >
            <ShieldAlert className='w-3.5 h-3.5' />
            Retirer ma fiche
          </Link>
        </div>
      </div>

      {/* Content */}
      <section className='px-6 py-12'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Left Column (2/3) */}
          <div className='lg:w-2/3 space-y-8'>
            {/* Présentation */}
            {club.description && (
              <div id='section-presentation' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
                <h2 className='text-2xl font-extrabold text-slate-900 mb-4'>Présentation</h2>
                <p className='text-slate-600 leading-relaxed'>{club.description}</p>
              </div>
            )}

            {/* Concours du club */}
            <div id='section-concours' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <div className='flex items-center justify-between mb-6'>
                <h2 className='text-2xl font-extrabold text-slate-900'>Concours du club</h2>
                <Link
                  to={ROUTES.SEARCH_CONCOURS}
                  className='text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1'
                >
                  Tous les concours <ArrowRight className='w-4 h-4' />
                </Link>
              </div>
              {clubCompetitions.length === 0 ? (
                <p className='text-slate-500 text-sm'>Aucune participation enregistrée pour le moment.</p>
              ) : (
                <div className='space-y-4'>
                  {clubCompetitions.map(c => (
                    <ClubCompetitionRow key={c.competitionId} entry={c} />
                  ))}
                </div>
              )}
            </div>

            {/* Blog */}
            <div id='section-blog' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <div className='flex items-center justify-between mb-6'>
                <h2 className='text-2xl font-extrabold text-slate-900'>Blog</h2>
                {articles.length > 0 && (
                  <Link
                    to={ROUTES.CLUB_BLOG.replace(':id', String(clubId))}
                    className='text-sm font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1'
                  >
                    Voir tous <ArrowRight className='w-4 h-4' />
                  </Link>
                )}
              </div>
              {articles.length === 0 ? (
                <div className='py-10 text-center'>
                  <FileText className='w-10 h-10 text-slate-200 mx-auto mb-3' />
                  <p className='text-slate-400 text-sm'>Aucun article publié pour le moment.</p>
                </div>
              ) : (
                <div className='grid md:grid-cols-2 gap-4'>
                  {articles.map(article => (
                    <Link
                      key={article.id}
                      to={ROUTES.ARTICLE.replace(':slug', article.slug)}
                      className='bg-slate-50 rounded-2xl p-5 hover:bg-slate-100 transition-colors group'
                    >
                      <h4 className='font-bold text-slate-900 group-hover:text-emerald-600 transition-colors line-clamp-2'>
                        {article.title}
                      </h4>
                      {article.excerpt && <p className='text-sm text-slate-500 mt-1 line-clamp-2'>{article.excerpt}</p>}
                      <div className='flex items-center gap-3 text-xs text-slate-400 mt-3'>
                        <span>{formatDateAbbreviatedFr(article.publishedAt ?? article.createdAt)}</span>
                        {article.category && (
                          <span className='px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-md'>
                            {article.category.name}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar (1/3) */}
          <div className='lg:w-1/3'>
            <div className='sticky top-36 space-y-6'>
              {/* Coordonnées */}
              <div id='section-contact' className='bg-white rounded-3xl border border-slate-200 p-6 scroll-mt-36'>
                <h3 className='font-bold text-lg text-slate-900 mb-4'>Coordonnées</h3>
                <div className='space-y-3'>
                  <div className='flex items-center gap-3 text-sm text-slate-600'>
                    <MapPin className='w-5 h-5 text-emerald-500 shrink-0' />
                    <span>
                      {club.address ? `${club.address}, ` : ''}
                      {club.city} ({club.department})
                    </span>
                  </div>
                  {club.phone && (
                    <div className='flex items-center gap-3 text-sm text-slate-600'>
                      <Phone className='w-5 h-5 text-emerald-500 shrink-0' />
                      <span>{club.phone}</span>
                    </div>
                  )}
                  {/*{club.email && (*/}
                  {/*  <div className='flex items-center gap-3 text-sm text-slate-600'>*/}
                  {/*    <Mail className='w-5 h-5 text-emerald-500 shrink-0' />*/}
                  {/*    <span className='truncate'>{club.email}</span>*/}
                  {/*  </div>*/}
                  {/*)}*/}
                  {isAuthenticated && club.email && (
                    <button
                      onClick={() => setContactOpen(true)}
                      className='w-full mt-2 bg-emerald-600 text-white py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 text-sm'
                    >
                      <Mail className='w-4 h-4' /> Envoyer un message
                    </button>
                  )}
                </div>
              </div>
              {(club.region ?? club.region) && (
                <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                  <h3 className='font-bold text-lg text-slate-900 mb-4'>Lieu & Accès</h3>
                  <div className='aspect-video relative bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center'>
                    <LeafletMap />
                  </div>
                </div>
              )}

              {/* CTA Rejoindre ou Claim Admin */}
              {club.hasAdmin && club.isActive ? (
                <JoinClubWidget clubId={clubId} clubName={club.name} />
              ) : (
                <ClaimAdminSection clubId={clubId} clubName={club.name} />
              )}

              {/* Partager */}
              <ShareBloc
                title='Partager ce club'
                shareText={`${club.name}${club.city ? ` — ${club.city}` : ''} | BoulesConnect`}
              />
            </div>
          </div>
        </div>
      </section>

      {contactOpen && (
        <ContactModal
          recipientName={club.name}
          senderName={[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
          isPending={contactMutation.isPending}
          onSend={contactMutation.mutate}
          onClose={() => setContactOpen(false)}
        />
      )}
    </div>
  );
}
